import { useState } from 'react';
import { useSelector } from 'react-redux';
import { SupportingDocumentFile } from '../../../../features/gms-proposal-forms/payment_term-loan_maturity-commission_services-notes-files/payment_term-loan_maturity-commission_services-notes-files.state';
import { selectedRequestAmendment } from '../../../../features/actuarial/request-amendment/request-amendment.slice';
import { getFileIcon } from '../../../ui/get-file-icon';
import { useToastify } from '../../../../library/toastify/toast-provider';
import { useDownloadAttachmentFileMutation } from '../../../../api/request-amendment/request-amendment-api';

interface Props {
    isShow: boolean;
    currentFiles: SupportingDocumentFile[];
    proposedFiles: SupportingDocumentFile[];
}

type FileChange = {
    key: string;
    name: string;
    current?: SupportingDocumentFile;
    proposed?: SupportingDocumentFile;
    status: 'ADDED' | 'REMOVED' | 'CHANGED';
};

const getFileRecord = (
    file: SupportingDocumentFile,
): Record<string, unknown> => {
    return file as unknown as Record<string, unknown>;
};

const getFileId = (file: SupportingDocumentFile): string => {
    const record = getFileRecord(file);

    const id =
        record.id ??
        record.file_id ??
        record.document_id ??
        record.attachment_id;

    if (id !== null && id !== undefined && String(id).trim()) {
        return String(id);
    }

    const name =
        record.fileName ??
        record.file_name ??
        record.filename ??
        record.original_name ??
        record.name;

    return name !== null && name !== undefined
        ? String(name).trim()
        : '';
};

const getFileName = (file?: SupportingDocumentFile): string => {
    if (!file) {
        return '';
    }

    const record = getFileRecord(file);

    const name =
        record.fileName ??
        record.file_name ??
        record.filename ??
        record.original_name ??
        record.name;

    return name !== null && name !== undefined
        ? String(name).trim()
        : 'Unnamed file';
};

const getFileSize = (file?: SupportingDocumentFile): string => {
    if (!file) {
        return '';
    }

    const record = getFileRecord(file);

    const size =
        record.file_size ??
        record.size ??
        record.fileSize;

    if (size === null || size === undefined || size === '') {
        return '';
    }

    const numericSize = Number(size);

    if (!Number.isFinite(numericSize)) {
        return String(size);
    }

    if (numericSize < 1024) {
        return `${numericSize} B`;
    }

    if (numericSize < 1024 * 1024) {
        return `${(numericSize / 1024).toFixed(1)} KB`;
    }

    return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileType = (file?: SupportingDocumentFile): string => {
    if (!file) {
        return '';
    }

    const record = getFileRecord(file);

    const type =
        record.file_type ??
        record.mime_type ??
        record.type;

    return type !== null && type !== undefined
        ? String(type)
        : '';
};

const normalizeFileForCompare = (
    file?: SupportingDocumentFile,
): Record<string, string> => {
    if (!file) {
        return {};
    }

    return {
        name: getFileName(file).toLowerCase(),
        size: getFileSize(file),
        type: getFileType(file).toLowerCase(),
    };
};

const areFilesEqual = (
    current?: SupportingDocumentFile,
    proposed?: SupportingDocumentFile,
): boolean => {
    return (
        JSON.stringify(normalizeFileForCompare(current)) ===
        JSON.stringify(normalizeFileForCompare(proposed))
    );
};

const getFileChanges = (
    currentFiles: SupportingDocumentFile[],
    proposedFiles: SupportingDocumentFile[],
): FileChange[] => {
    const currentMap = new Map<string, SupportingDocumentFile>();
    const proposedMap = new Map<string, SupportingDocumentFile>();

    currentFiles.forEach((file, index) => {
        const key = getFileId(file) || `${getFileName(file)}-${index}`;
        currentMap.set(key, file);
    });

    proposedFiles.forEach((file, index) => {
        const key = getFileId(file) || `${getFileName(file)}-${index}`;
        proposedMap.set(key, file);
    });

    const changes: FileChange[] = [];

    proposedMap.forEach((proposed, key) => {
        const current = currentMap.get(key);

        if (!current) {
            changes.push({
                key,
                name: getFileName(proposed),
                proposed,
                status: 'ADDED',
            });

            return;
        }

        if (!areFilesEqual(current, proposed)) {
            changes.push({
                key,
                name: getFileName(proposed),
                current,
                proposed,
                status: 'CHANGED',
            });
        }
    });

    currentMap.forEach((current, key) => {
        if (!proposedMap.has(key)) {
            changes.push({
                key,
                name: getFileName(current),
                current,
                status: 'REMOVED',
            });
        }
    });

    return changes;
};

const FileInfo = ({file} : {file?: SupportingDocumentFile}) => {

    const { showToastNotify } = useToastify();
    const [downloadFile, { isLoading: isLoadingDownloadFile }] = useDownloadAttachmentFileMutation();

    if (!file) {
        return (
            <span className="text-[10px] italic text-slate-400">
                Not applicable
            </span>
        );
    }

    const amendId = file.supportingId;
    const fileName = getFileName(file);
    const fileSize = getFileSize(file);

    const record = getFileRecord(file);
    const filePath = record.filePath ?? record.file_path;

    const handleDownload = async () => {
        if (isLoadingDownloadFile) {
            return;
        }

        if (amendId === null || amendId === undefined) {
            showToastNotify(
                'Unable to download the file. Amendment ID is missing.',
                'warning',
            );
            return;
        }

        try {
            const fileUrl = await downloadFile({
                amendId: Number(amendId),
            }).unwrap();

            const link = document.createElement('a');

            link.href = fileUrl;
            link.download = fileName || 'download';
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                URL.revokeObjectURL(fileUrl);
            }, 1000);
        } catch (error) {
            showToastNotify(
                'Failed to download attachment.',
                'warning',
            );
        }
    };

    return (
        <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#eef6fc] text-[#186cb2]">
                {getFileIcon(fileName)}
            </div>

            <div className="min-w-0 flex-1 text-center">
                <div className="break-words text-start text-[11px] font-medium text-slate-700">
                    {fileName || 'Unnamed file'}
                </div>
                {fileSize && (
                    <div className="mt-0.5 text-[9px] text-slate-400">
                        {fileSize}
                    </div>
                )}
            </div>
            <>
                {filePath && (
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={isLoadingDownloadFile}
                        title={isLoadingDownloadFile ? 'Downloading...' : 'Download file'}
                        aria-label={`Download ${fileName || 'file'}`}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-[#eef6fc] hover:text-[#186cb2] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isLoadingDownloadFile ? (
                            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 animate-spin" aria-hidden="true" >
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" className="opacity-25" />
                                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true" >
                                <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                )}
            </>
        </div>
    );
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg
                viewBox="0 0 24 24"
                fill="none"
                className="h-4 w-4 shrink-0"
                aria-hidden="true"
            >
                <path
                    d="m5 12.5 4.5 4L19 7"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>

            <span>No changes</span>
        </div>
    );
};

export default function ProposedSupportingDocuments({
    isShow,
    currentFiles,
    proposedFiles,
}: Props) {
    const [showContent, setShowContent] = useState(true);
    
    const changes = getFileChanges(
        currentFiles ?? [],
        proposedFiles ?? [],
    );

    if (!isShow || changes.length === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`}
                            aria-hidden="true"
                        >
                            <path
                                d="m7 10 5 5 5-5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Supporting Documents
                        </h3>

                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>

                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>

                {showContent && (
                    <>
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>

                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((change) => (
                                            <div
                                                key={`current-${change.key}`}
                                                className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0"
                                            >
                                                <div className="mb-1 flex items-center gap-2">
                                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-slate-500">
                                                        {change.status === 'REMOVED'
                                                            ? 'REMOVED'
                                                            : change.status === 'CHANGED'
                                                                ? 'CHANGED'
                                                                : 'CURRENT'}
                                                    </span>
                                                </div>

                                                <FileInfo file={change.current} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="min-w-0 p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="space-y-3">
                                        {changes.map((change) => (
                                            <div
                                                key={`proposed-${change.key}`}
                                                className="border-b border-blue-50 pb-3 last:border-b-0 last:pb-0"
                                            >
                                                <div className="mb-1 flex items-center gap-2">
                                                    <span className="rounded bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#186cb2]">
                                                        {change.status === 'ADDED'
                                                            ? 'ADDED'
                                                            : change.status === 'CHANGED'
                                                                ? 'CHANGED'
                                                                : 'PROPOSED'}
                                                    </span>
                                                </div>

                                                <FileInfo file={change.proposed} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}